import { forwardRef, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import * as d3 from 'd3'

import { formatSVG } from '@aytm/app/stats/lib/render/svg-export'
import { useExport } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useExport'

import d3v3 from '@aytm/app/stats/components/topography/d3v3'
import PMapTopography from '@aytm/app/stats/components/topography/PMapTopography'

import {
  applyAttributesVisibility,
  applyBrandsVisibility,
  computeFlags,
  computePins
} from '@aytm/app/stats/page/question-views/bpm/components/tv/components/chart/lib/tvPinsFlags'
import { resolveScene } from '@aytm/app/stats/page/question-views/bpm/components/tv/components/chart/lib/tvData'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import { BPM_EXPORT_OPTIONS } from '@aytm/app/stats/page/question-views/bpm/lib/exportOptions'

import style from '@aytm/app/stats/page/question-views/bpm/components/tv/components/chart/styles/Chart.module.scss'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'

// First paint, before the box is measured.
const DEFAULT_WIDTH = 814

const CHART_HEIGHT = 512
const COMPLETE_DELAY = 300

// Top-level groups PMapTopography appends to the <svg>; cleared when blanking the chart.
const TOPOGRAPHY_NODES = 'rect.pmap-topography-bg, g.pmap-topography-surface3d, g.pmap-topography-legend'

// Thin React wrapper around the legacy d3 v3 PMapTopography engine (bundled as modules in
// app/stats/components/topography — no window globals). The engine is v3-only (surface3D /
// d3.scale / d3.mouse / d3.event) — it MUST be fed a selection from the same module-scoped
// v3 instance (`d3v3`), not the npm v7 `d3`. See tv-chart.md § Нюанси порту (d3-гібрид).
//
// The instance is created ONCE and never recreated (legacy never does `new` twice). Subsequent
// prop changes drive imperative setters. Each legacy Backbone listener maps to one update path here,
// keyed on which prop changed — so the engine receives the exact setter sequence it did before,
// including the legacy path-dependent differences (see tv-chart.md § Рендеринг).
const BPMTvChart = forwardRef(
  (
    {
      questionKey,
      questionId,
      questionVariants,
      batches,
      answer,
      colorTheme,
      // eslint-disable-next-line no-unused-vars -- accepted for parity; the TV surface does not read legendMode
      chartLegendMode,
      chartMode,
      tvPlotByValue,
      tvElevationsRepresentValue,
      tvBrandsValue = [],
      tvAttributesValue = [],
      defaultValues,
      features,
      onComplete,
      onChartReady
    },
    ref
  ) => {
    // the scene is built to a pixel width, so the box is measured rather than handed down -- off the
    // caller's own node when it hands a ref in
    const { componentRef, width } = useComponentWidth({ ref, defaultWidth: DEFAULT_WIDTH })

    const apiRef = useRef({})

    const svgRef = useRef(null)
    const topographyRef = useRef(null)
    const prevRef = useRef(null) // last prop snapshot, for "which prop changed" detection
    // completion must survive effect re-runs (a width re-measure re-runs the effect inside the
    // 300ms window on pages without a .-main column, e.g. the render shell) — cleared on unmount only
    const completeTimerRef = useRef(null)

    // Adapter so the Highcharts useExport hook can read our own <svg> (chart.container.firstChild).
    const exportAdapterRef = useRef({
      chart: {
        container: {
          get firstChild() {
            return svgRef.current
          }
        }
      }
    })
    const { exportOptions, handleExport } = useExport({
      questionKey,
      chartRef: exportAdapterRef,
      qType: 'bpm',
      features,
      baseOptions: BPM_EXPORT_OPTIONS
    })

    // Base pins/flags (text + colors + grid coords); visibility/elevation filtering applied per path.
    const basePins = useMemo(
      () => computePins({ answer, questionVariants, colorTheme }),
      [answer, questionVariants, colorTheme]
    )
    const baseFlags = useMemo(() => computeFlags({ answer, batches }), [answer, batches])

    // PPTX bridge (pptx.coffee -> block.formatSVG). The svg transform + serialization live here
    // (lib/svg-export formatSVG); coffee just forwards the string. Pins label fonts (the export loses
    // the page stylesheet) and strips the interactive download / hidden-pin affordances.
    apiRef.current.formatSVG = () => {
      const source = svgRef.current
      if (!source) return null

      const svg = d3.select(source.cloneNode(true))

      svg
        .selectAll('text.pin-label-text, text.flag-label')
        .style('font-family', 'Ubuntu, Arial')
        .style('font-size', '11px')
        .style('line-height', '13px')
      svg
        .selectAll(
          'text.pmap-topography-legend-title, text.pmap-topography-legend-max, text.pmap-topography-legend-min'
        )
        .style('font-family', 'Ubuntu, Arial')
        .style('font-size', '13px')
        .style('line-height', '15px')

      svg.selectAll("[id^='pmaptopo-download-on-']").remove()
      svg.selectAll("[id^='pmaptopo-download-click-']").remove()
      svg.selectAll('.pin-label.pin-label-hidden').remove()
      svg.selectAll('g.download').remove()
      svg.selectAll('text.pmap-topography-legend-title a').remove()

      return formatSVG(svg.node())
    }

    useEffect(() => {
      const svgNode = svgRef.current
      if (!svgNode) return

      const prev = prevRef.current
      const cur = {
        answer,
        colorTheme,
        chartLegendMode,
        chartMode,
        tvPlotByValue,
        tvElevationsRepresentValue,
        tvBrandsValue,
        tvAttributesValue
      }
      prevRef.current = cur
      const changed = key => prev && prev[key] !== cur[key]

      // chartMode map/none → blank surface, drop the instance (next non-map/none recreates).
      if (isMapOrNone(chartMode)) {
        d3v3.select(svgNode).selectAll(TOPOGRAPHY_NODES).remove()
        topographyRef.current = null
        clearTimeout(completeTimerRef.current)
        onComplete?.()
        return
      }

      const sceneArgs = {
        answer,
        tvPlotByValue,
        tvElevationsRepresentValue,
        defaultValues,
        questionVariants,
        batches
      }

      // CREATION — mirrors first `renderChart` (constructor gets meanList-filtered pins AND flags).
      if (!topographyRef.current) {
        applyBrandsVisibility(basePins, tvBrandsValue)
        applyAttributesVisibility(baseFlags, tvAttributesValue)
        const scene = resolveScene({ ...sceneArgs, pins: basePins, flags: baseFlags, mode: 'render' })
        if (!scene) {
          // only the engine sizes the plate, so with no scene to hand it the plate would fall to the
          // browser's 300px default; it follows the measured box until an engine takes it over
          svgNode.setAttribute('width', width)
          onComplete?.()
          return
        }
        topographyRef.current = new PMapTopography({
          svg: d3v3.select(svgNode),
          data: scene.data,
          pins: scene.pins,
          flags: scene.flags,
          version: answer?.mds?.version || 1,
          legend: scene.legend,
          width: width,
          height: CHART_HEIGHT
        })
        clearTimeout(completeTimerRef.current)
        completeTimerRef.current = setTimeout(() => onComplete?.(), COMPLETE_DELAY)
        return
      }

      const topo = topographyRef.current

      // REBUILD — mirrors render() on answer/colorTheme/legendMode change: visibility setters, then the
      // renderChart-update sequence (setLegend → setPins → setData, NO setFlags; only setData redraws).
      if (changed('answer') || changed('colorTheme') || changed('chartLegendMode')) {
        applyBrandsVisibility(basePins, tvBrandsValue)
        applyAttributesVisibility(baseFlags, tvAttributesValue)
        topo.setPins(basePins, true)
        topo.setFlags(baseFlags, true)
        const scene = resolveScene({ ...sceneArgs, pins: basePins, flags: baseFlags, mode: 'render' })
        if (!scene) {
          onComplete?.()
          return
        }
        topo.setLegend(scene.legend)
        topo.setPins(scene.pins)
        topo.setData(scene.data, true)
        clearTimeout(completeTimerRef.current)
        completeTimerRef.current = setTimeout(() => onComplete?.(), COMPLETE_DELAY)
        return
      }

      // ELEVATION — mirrors change:tvElevationsRepresentValue listener: NO meanList filter, generic
      // legend text, and setData(redraw) resets the 3D orientation (parity). plotBy switch routes here too.
      if (changed('tvElevationsRepresentValue') || changed('tvPlotByValue')) {
        applyBrandsVisibility(basePins, tvBrandsValue)
        applyAttributesVisibility(baseFlags, tvAttributesValue)
        const scene = resolveScene({ ...sceneArgs, pins: basePins, flags: baseFlags, mode: 'elevation' })
        if (!scene) return
        topo.setLegend(scene.legend)
        topo.setPins(scene.pins)
        topo.setFlags(scene.flags)
        topo.setData(scene.data, true)
        return
      }

      // VISIBILITY — mirrors setBrandsVisibility / setAttributesVisibility: toggle on the full arrays,
      // setPins/setFlags with redraw (orientation preserved — no setData).
      if (changed('tvBrandsValue')) {
        applyBrandsVisibility(basePins, tvBrandsValue)
        topo.setPins(basePins, true)
        return
      }
      if (changed('tvAttributesValue')) {
        applyAttributesVisibility(baseFlags, tvAttributesValue)
        topo.setFlags(baseFlags, true)
        return
      }

      return
    }, [
      answer,
      questionVariants,
      batches,
      colorTheme,
      chartLegendMode,
      chartMode,
      tvPlotByValue,
      tvElevationsRepresentValue,
      tvBrandsValue,
      tvAttributesValue,
      defaultValues,
      width,
      basePins,
      baseFlags,
      onComplete
    ])

    useEffect(() => () => clearTimeout(completeTimerRef.current), [])

    useEffect(() => {
      onChartReady?.(apiRef.current)
    }, [onChartReady])

    return (
      <div ref={componentRef} className={style.chart}>
        <div className={style.export}>
          <ExportDropdown options={exportOptions} onExport={handleExport} />
        </div>
        <div id={questionId == null ? undefined : `chart_question_${questionId}`}>
          <svg
            ref={svgRef}
            className={style.topography}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            preserveAspectRatio="xMidYMid meet"
            height={CHART_HEIGHT}
            style={{ overflow: 'hidden' }}
          >
            <defs>
              <style>{`
            .pmap-topography-surface3d .pin-label,
            .pmap-topography-surface3d .flag-label {
              font-family: Ubuntu, Calibri, Segoe, Helvetica, Arial;
              font-size: 11px;
              line-height: 13px;
              cursor: default;
            }
            .pmap-topography-surface3d .pin-label.pin-label-hidden { display: none; }
            .pmap-topography-legend .pmap-topography-legend-title,
            .pmap-topography-legend .pmap-topography-legend-max,
            .pmap-topography-legend .pmap-topography-legend-min {
              font-family: Ubuntu, Calibri, Segoe, Helvetica, Arial;
              font-size: 13px;
              line-height: 15px;
              cursor: default;
            }
          `}</style>
            </defs>
          </svg>
        </div>
      </div>
    )
  }
)

BPMTvChart.propTypes = {
  questionKey: PropTypes.string,
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionVariants: PropTypes.array.isRequired,
  batches: PropTypes.array.isRequired,
  answer: answerPropType,
  colorTheme: PropTypes.array.isRequired,
  chartLegendMode: PropTypes.string,
  chartMode: PropTypes.string,
  tvPlotByValue: PropTypes.string,
  tvElevationsRepresentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tvBrandsValue: PropTypes.array,
  tvAttributesValue: PropTypes.array,
  defaultValues: PropTypes.object.isRequired,
  features: PropTypes.object,
  onComplete: PropTypes.func,
  onChartReady: PropTypes.func
}

BPMTvChart.displayName = 'BPMTvChart'

export default BPMTvChart
